import { Button, Modal, ModalFooter, ModalHeader } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tableColumnGroupsAtom } from '@/atoms/queries/tableColumns';
import {
  clientOrderByAtom,
  clientOrderDirectionAtom,
  defaultTableColumnsAtom,
  sortAtom,
  visibleTableColumnsAtom,
  tableConfigAtom,
  tableIdAtom,
} from '@/atoms/table';
import { LOCKED_COLUMN_BY_TABLE_ID } from '@/consts/dataLocker/table';
import { useDeleteUserConfig } from '@/hooks/useDeleteUserConfig';
import { useUpsertUserConfig } from '@/hooks/useUpsertUserConfig';
import { UserConfigType } from '@/types/userConfigs';
import { isOnDemandMetricKey } from '@/utils/metrics';
import { withLockedColumn } from '@/utils/table';
import { ColumnSelect } from './ColumnSelect';
import { ColumnSort } from './ColumnSort';
import { MobileTableColumnsEditorTabs } from './MobileTableColumnsEditorTabs';

export const EditTableColumnsModal = ({
  setShowEditColumnsModal,
}: {
  setShowEditColumnsModal: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation(['table', 'general']);

  const columnGroups = useAtomValue(tableColumnGroupsAtom);
  const [tableVisibleColumns, setTableVisibleColumns] = useAtom(visibleTableColumnsAtom);
  const tableId = useAtomValue(tableIdAtom);
  const lockedKey = tableId ? LOCKED_COLUMN_BY_TABLE_ID[tableId] : undefined;
  const lockedColumns = lockedKey ? [lockedKey] : [];
  const [modalVisibleColumns, setModalVisibleColumns] = useState(() =>
    withLockedColumn(tableVisibleColumns, lockedKey),
  );
  const defaultColumns = useAtomValue(defaultTableColumnsAtom);
  const defaultColumnsWithLocked = withLockedColumn(defaultColumns, lockedKey);
  const { configId } = useAtomValue(tableConfigAtom);
  const upsertUserConfig = useUpsertUserConfig();
  const deleteUserConfig = useDeleteUserConfig();
  const clientOrderBy = useAtomValue(clientOrderByAtom);
  const clientOrderDirection = useAtomValue(clientOrderDirectionAtom);
  const setSort = useSetAtom(sortAtom);

  const handleCancelModal = () => {
    setModalVisibleColumns(withLockedColumn(tableVisibleColumns, lockedKey));
    setShowEditColumnsModal(false);
  };

  const handleConfirmModal = () => {
    const persistedColumns = withLockedColumn(modalVisibleColumns, lockedKey);
    // keep an on-demand metric's sort on the server, so its request still includes it once the column is hidden
    if (clientOrderBy && isOnDemandMetricKey(clientOrderBy) && !persistedColumns.includes(clientOrderBy)) {
      setSort({ orderBy: clientOrderBy, direction: clientOrderDirection });
    }
    setTableVisibleColumns(persistedColumns);
    setShowEditColumnsModal(false);

    if (tableId) {
      const defaultColumnsSelected = defaultColumnsWithLocked.toString() === persistedColumns.toString();

      // In this function, we don't want to save the defaults to the DB as these will be the defaults for that moment in time.
      // By not saving or deleting these, should the table's default columns change the user will always see the up-to-date defaults.

      // Delete configs which match the table defaults
      if (configId && defaultColumnsSelected) {
        deleteUserConfig(configId);
        return;
      }

      // Do nothing if user has no saved config for this table and has selected the defaults
      if (defaultColumnsSelected) return;

      // Save user selection
      upsertUserConfig({
        configId,
        configType: UserConfigType.TABLE_COLUMNS,
        configDefinition: { tableId, selectedColumns: persistedColumns },
      });
    }
  };

  return (
    <Modal
      ariaLabelledBy="modal-edit-columns"
      isOpen
      size="small"
      onCancel={handleCancelModal}
      onConfirm={handleConfirmModal}
    >
      <ModalHeader id="modal-edit-columns">{t('editColumns')}</ModalHeader>
      <MobileTableColumnsEditorTabs />
      <div className="flex h-[420px]">
        <ColumnSelect
          columnGroups={columnGroups}
          visibleColumns={modalVisibleColumns}
          setVisibleColumns={setModalVisibleColumns}
          lockedColumns={lockedColumns}
        />
        <ColumnSort
          visibleColumns={modalVisibleColumns}
          setVisibleColumns={setModalVisibleColumns}
          defaultColumns={defaultColumnsWithLocked}
          lockedColumns={lockedColumns}
        />
      </div>
      <ModalFooter>
        <Button onClick={handleCancelModal} variant="ghost">
          {t('cancel', { ns: 'general' })}
        </Button>
        <Button
          onClick={handleConfirmModal}
          variant="primary"
          disabled={modalVisibleColumns.length <= lockedColumns.length}
        >
          {t('apply', { ns: 'general' })}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
